import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import type { ApiKey } from "./types";
import * as settingsApi from "./settings-api";

interface ApiErrorBody {
  detail?: string;
}

const errorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return error.response?.data?.detail || fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

const toApiKey = (key: settingsApi.DeveloperApiKeyRecord): ApiKey => ({
  id: key.api_key_id,
  name: key.name,
  masked: `${key.key_prefix}••••••••`,
  createdAt: key.created_at,
  lastUsedAt: key.last_used_at,
});

export function useApiKeys() {
  const { token } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [maxActiveKeys, setMaxActiveKeys] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setKeys([]);
      setActiveCount(0);
      setLoading(false);
      setError("Authentication required");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await settingsApi.getDeveloperApiKeys(token);
      setKeys(data.api_keys.map(toApiKey));
      setActiveCount(data.active_count);
      setMaxActiveKeys(data.max_active_keys);
    } catch (requestError) {
      setError(errorMessage(requestError, "Failed to load API keys"));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createKey = useCallback(async () => {
    if (!token) throw new Error("Authentication required");
    try {
      const created = await settingsApi.createDeveloperApiKey(token);
      await refresh();
      return created.api_key;
    } catch (requestError) {
      throw new Error(errorMessage(requestError, "Failed to create API key"));
    }
  }, [refresh, token]);

  const revokeKey = useCallback(async (apiKeyId: string) => {
    if (!token) throw new Error("Authentication required");
    try {
      await settingsApi.deleteDeveloperApiKey(apiKeyId, token);
      await refresh();
    } catch (requestError) {
      throw new Error(errorMessage(requestError, "Failed to revoke API key"));
    }
  }, [refresh, token]);

  return {
    keys,
    activeCount,
    maxActiveKeys,
    loading,
    error,
    refresh,
    createKey,
    revokeKey,
  };
}
